import { ProjectRecord } from "@/types";
import { updateProject } from "@/api/projectsApi";
import { updateContractorProjectReq } from "@/api/contractorApi";

export interface UpdateEnrolmentStatusParams {
  projectId: ProjectRecord["id"];
  enrolmentStatus: string;
  isContractor: boolean;
  onReject?: (projectId: number) => void;
  onCorrectionNeeded?: (projectId: number) => void;
  onSuccess?: () => void | Promise<void>;
  onError?: (error: string) => void;
}

/**
 * Core logic for updating enrollment status.
 * Handles special cases (rejected, correction_needed) and API calls.
 * Can be used in tables, detail pages, or anywhere enrollment status is updated.
 */
export const updateEnrolmentStatusUtil = async ({
  projectId,
  enrolmentStatus,
  isContractor,
  onReject,
  onCorrectionNeeded,
  onSuccess,
  onError,
}: UpdateEnrolmentStatusParams): Promise<void> => {
  try {
    // Handle special status changes that require modals
    switch (enrolmentStatus) {
      case "rejected": {
        onReject?.(projectId);
        return;
      }
      case "correction_needed": {
        onCorrectionNeeded?.(projectId);
        return;
      }
      default: {
        // Standard status update
        const formData = new FormData();
        formData.append("enrolmentStatus", enrolmentStatus);

        if (isContractor) {
          await updateContractorProjectReq(projectId, formData);
        } else {
          await updateProject(projectId, formData);
        }

        onSuccess?.();
      }
    }
  } catch (e: any) {
    console.error(e);
    const errorMsg =
      e?.response?.data?.error ||
      e?.response?.data?.message ||
      e?.message ||
      "Failed to update enrollment status. Please try again.";
    onError?.(errorMsg);
  }
};
